/**
 * Last-used match memory — remembers the match a coach last worked on
 * in Coach Review, per team, so a bare `/coach/review` mount can
 * re-open on that match instead of an empty picker. Small quality-of-
 * life carry-through (A3): a coach who reviews one game across several
 * sessions doesn't re-pick it every time.
 *
 * Storage: `localStorage` (persists across browser sessions — unlike
 * the per-tab composer drafts, the "which match was I on" preference
 * should outlive a tab close). Key `tktic_cr_last_match:<teamId>`
 * follows the `tktic_*` namespace from `playbackMemory.ts`, scoped per
 * team so switching teams doesn't surface another team's match.
 *
 * The caller is responsible for re-validating that the remembered id
 * still exists in the current matches list before applying it — a
 * deleted match must not leave the picker pointing at a ghost.
 *
 * All access is try/catch-wrapped (private mode / quota → no-op).
 */

const KEY_PREFIX = "tktic_cr_last_match";

function key(teamId: string): string {
  return `${KEY_PREFIX}:${teamId}`;
}

/** Remember the last match the coach worked on for this team. A blank
 * teamId or matchId is ignored. */
export function rememberLastMatch(
  teamId: string | null | undefined,
  matchId: string | null | undefined,
): void {
  if (!teamId || !matchId) return;
  try {
    localStorage.setItem(key(teamId), matchId);
  } catch {
    // private mode / quota — no-op.
  }
}

/** Read the remembered match id for this team, or null. The caller
 * MUST confirm the id still exists in the live matches list before
 * seeding the picker. */
export function getLastMatch(teamId: string | null | undefined): string | null {
  if (!teamId) return null;
  try {
    return localStorage.getItem(key(teamId)) || null;
  } catch {
    return null;
  }
}
